import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "helpers/jwt-token-access/customBaseQuery";
import { method } from "lodash";

const convertToFormData = (obj: any) => {
  const formData = new FormData();
  Object.keys(obj).forEach((key) => {
    if (Array.isArray(obj[key])) {
      obj[key].forEach((item: any) => formData.append(key, item));
    } else {
      formData.append(key, obj[key]);
    }
  });
  return formData;
};

export const resourceInventoryManagementSlice = createApi({
  reducerPath: "resourceInventoryManagementAPI",
  baseQuery: customBaseQuery,
  tagTypes: [
    "GetInventoryProducts",
    "GetInventoryProductByNumber",
    "GetPurchaseOrderByNumber",
    "GetPurchaseOrders",
    "GetPurchaseOrderByRequestId",
    "GetPurchaseOrderAuditLogs",
    "GetStockDetails",
    "GetStockManagementByNumber",
    "GetStockManagement",
    "StockProjectAllocations",
    "StockProjectAllocation",
    "StockAllocationManagement",
    "PdfMetadata",
    "DocumentChecklist",
    "ChecklistMappings",
    "ChecklistDocuments",
  ],
  endpoints: (builder) => ({
    //Suppliers
    getSuppliers: builder.query({
      query: (params: { page?: number; pageSize?: number; search?: string } | void) => {
        const page = params?.page ?? 1;
        const pageSize = params?.pageSize ?? 30;
        const search = params?.search ?? "";
        const qs = `?page=${page}&page_size=${pageSize}${
          search ? `&search=${encodeURIComponent(search)}` : ""
        }`;
        return `/inventory/api/v1/suppliers/${qs}`;
      },
    }),
    getSupplierById: builder.query({
      query: (id) => `/inventory/api/v1/suppliers/${id}/`,
    }),
    createSupplier: builder.mutation({
      query: (newSupplier) => ({
        url: "/inventory/api/v1/suppliers/",
        method: "POST",
        body: newSupplier,
      }),
    }),
    updateSupplier: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/suppliers/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),
    deleteSupplierById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/suppliers/${id}/`,
        method: "DELETE",
      }),
    }),

    // Products
    getProducts: builder.query({
      query: (page) =>
        `/inventory/api/v1/products/?page=${page.page}&page_size=${
          page.pageSize
        }&status=${page.status}&search=${page.search || ""}&category=${
          page.category || ""
        }&tag=${page.tag || ""}&project=${page.project || ""}&purchase_request=${
          page.purchase_request || ""
        }`,
      providesTags: ["GetInventoryProducts"],
    }),
    getProductById: builder.query({
      query: (id) => `/inventory/api/v1/products/${id}/`,
      providesTags: (result, error, id) => [
        { type: "GetInventoryProductByNumber", id: id },
      ],
    }),
    createProduct: builder.mutation({
      query: (formData: FormData) => ({
        url: "/inventory/api/v1/products/",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),
    updateProduct: builder.mutation({
      query: ({ id, productData }) => ({
        url: `/inventory/api/v1/products/${id}/`,
        method: "PATCH",
        body: productData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "GetInventoryProductByNumber", id: id },
        { type: "GetInventoryProducts" },
      ],
    }),
    deleteProductById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/products/${id}/`,
        method: "DELETE",
      }),
    }),

    getPurchaseRequests: builder.query({
      query: (page) =>
        `/inventory/api/v1/purchase-requests/?page=${page.page}&page_size=${
          page.pageSize
        }&status=${page.status || ""}&search=${page.search || ""}&project=${
          page.project || ""
        }`,
      providesTags: ["GetInventoryProducts"],
    }),
    getPurchaseRequestById: builder.query({
      query: (id) => `/inventory/api/v1/purchase-requests/${id}/`,
      providesTags: (result, error, id) => [
        { type: "GetInventoryProductByNumber", id: id },
      ],
    }),
    createPurchaseRequest: builder.mutation({
      query: (payload) => ({
        url: "/inventory/api/v1/purchase-requests/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),
    updatePurchaseRequest: builder.mutation({
      query: ({ id, productData }) => ({
        url: `/inventory/api/v1/purchase-requests/${id}/`,
        method: "PATCH",
        body: productData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "GetInventoryProductByNumber", id: id },
        { type: "GetInventoryProducts" },
      ],
    }),
    deletePurchaseRequestById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/purchase-requests/${id}/`,
        method: "DELETE",
      }),
    }),

    getPurchaseRequestItems: builder.query({
      query: (page) =>
        `/inventory/api/v1/purchase-request-items/?page=${page.page}&page_size=${page.pageSize}`,
      providesTags: ["GetInventoryProducts"],
    }),
    getPurchaseRequestItemsById: builder.query({
      query: (id) => `/inventory/api/v1/purchase-request-items/${id}/`,
      providesTags: (result, error, id) => [
        { type: "GetInventoryProductByNumber", id: id },
      ],
    }),
    createPurchaseRequestItems: builder.mutation({
      query: (itemsPayload) => ({
        url: "/inventory/api/v1/purchase-request-items/",
        method: "POST",
        body: itemsPayload,
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),
    bulkMarkRfq: builder.mutation({
      query: (body: { item_ids: number[]; is_rfq?: boolean }) => ({
        url: "/inventory/api/v1/purchase-request-items/bulk-mark-rfq/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),
    updatePurchaseRequestItems: builder.mutation({
      query: ({ id, productData }) => ({
        url: `/inventory/api/v1/purchase-request-items/${id}/`,
        method: "PATCH",
        body: productData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "GetInventoryProductByNumber", id: id },
        { type: "GetInventoryProducts" },
      ],
    }),
    deletePurchaseRequestItemsById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/purchase-request-items/${id}/`,
        method: "DELETE",
      }),
    }),

    // Measurements
    getMeasurements: builder.query({
      query: (params?: { page?: number; pageSize?: number; search?: string }) => {
        const page = params?.page ?? 1;
        const pageSize = params?.pageSize ?? 30;
        const search = params?.search ? `&search=${encodeURIComponent(params.search)}` : "";
        return `/inventory/api/v1/measurements/?page=${page}&page_size=${pageSize}${search}`;
      },
    }),
    getMeasurementById: builder.query({
      query: (id) => `/inventory/api/v1/measurements/${id}/`,
    }),
    createMeasurement: builder.mutation({
      query: (values) => ({
        url: "/inventory/api/v1/measurements/",
        method: "POST",
        body: values,
      }),
    }),
    updateMeasurement: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/measurements/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),
    deleteMeasurementById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/measurements/${id}/`,
        method: "DELETE",
      }),
    }),

    // Categories
    getCategories: builder.query({
      query: (params?: { page?: number; pageSize?: number; search?: string }) => {
        const page = params?.page ?? 1;
        const pageSize = params?.pageSize ?? 30;
        const search = params?.search ? `&search=${encodeURIComponent(params.search)}` : "";
        return `/inventory/api/v1/categories/?page=${page}&page_size=${pageSize}${search}`;
      },
    }),
    getCategoryById: builder.query({
      query: (id) => `/inventory/api/v1/categories/${id}/`,
    }),
    createCategory: builder.mutation({
      query: (values) => ({
        url: "/inventory/api/v1/categories/",
        method: "POST",
        body: values,
      }),
    }),
    updateCategory: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/categories/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),
    deleteCategoryById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/categories/${id}/`,
        method: "DELETE",
      }),
    }),

    //Suppliers
    getTags: builder.query({
      query: (params?: { page?: number; pageSize?: number; search?: string }) => {
        const page = params?.page ?? 1;
        const pageSize = params?.pageSize ?? 50;
        const search = params?.search ? `&search=${encodeURIComponent(params.search)}` : "";
        return `/inventory/api/v1/tags/?page=${page}&page_size=${pageSize}${search}`;
      },
    }),
    getTagsById: builder.query({
      query: (id) => `/inventory/api/v1/tags/${id}/`,
    }),
    createTags: builder.mutation({
      query: (Tags) => ({
        url: "/inventory/api/v1/tags/",
        method: "POST",
        body: Tags,
      }),
    }),
    updateTags: builder.mutation({
      query: ({ id, data }) => ({
        url: `/inventory/api/v1/tags/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    deleteTagsById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/tags/${id}/`,
        method: "DELETE",
      }),
    }),

    // PDF Metadata
    getPdfMetadataList: builder.query({
      query: () => "/organization/api/v1/pdf-metadata/",
      providesTags: (result: any) =>
        result?.results
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: "PdfMetadata" as const,
                id,
              })),
              { type: "PdfMetadata", id: "LIST" },
            ]
          : [{ type: "PdfMetadata", id: "LIST" }],
    }),
    getPdfMetadataById: builder.query({
      query: (id: number | string) => `/organization/api/v1/pdf-metadata/${id}/`,
      providesTags: (result, error, id) => [{ type: "PdfMetadata", id }],
    }),
    createPdfMetadata: builder.mutation({
      query: (body) => ({
        url: "/organization/api/v1/pdf-metadata/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PdfMetadata", id: "LIST" }],
    }),
    updatePdfMetadata: builder.mutation({
      query: ({ id, data }) => ({
        url: `/organization/api/v1/pdf-metadata/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "PdfMetadata", id },
        { type: "PdfMetadata", id: "LIST" },
      ],
    }),
    deletePdfMetadata: builder.mutation({
      query: (id) => ({
        url: `/organization/api/v1/pdf-metadata/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "PdfMetadata", id: "LIST" }],
    }),

    // Document Checklist (master)
    getDocumentChecklist: builder.query({
      query: (params?: { page?: number; page_size?: number; search?: string }) => {
        const page = params?.page ?? 1;
        const pageSize = params?.page_size ?? 10;
        const search = params?.search ? `&search=${encodeURIComponent(params.search)}` : "";
        return `/inventory/api/v1/document-checklist/?page=${page}&page_size=${pageSize}${search}`;
      },
      providesTags: (result: any) =>
        result?.results
          ? [
              ...result.results.map(({ id }: { id: number }) => ({
                type: "DocumentChecklist" as const,
                id,
              })),
              { type: "DocumentChecklist", id: "LIST" },
            ]
          : [{ type: "DocumentChecklist", id: "LIST" }],
    }),
    getDocumentChecklistById: builder.query({
      query: (id: number | string) => `/inventory/api/v1/document-checklist/${id}/`,
      providesTags: (result, error, id) => [{ type: "DocumentChecklist", id }],
    }),
    createDocumentChecklist: builder.mutation({
      query: (body: FormData) => ({
        url: "/inventory/api/v1/document-checklist/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "DocumentChecklist", id: "LIST" }],
    }),
    updateDocumentChecklist: builder.mutation({
      query: ({ id, body }: { id: number; body: FormData }) => ({
        url: `/inventory/api/v1/document-checklist/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "DocumentChecklist", id },
        { type: "DocumentChecklist", id: "LIST" },
      ],
    }),
    deleteDocumentChecklist: builder.mutation({
      query: (id: number) => ({
        url: `/inventory/api/v1/document-checklist/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "DocumentChecklist", id },
        { type: "DocumentChecklist", id: "LIST" },
      ],
    }),

    // Checklist Mappings (product <> document checklist)
    getChecklistMappingsByProduct: builder.query({
      query: (productId: number | string) =>
        `/inventory/api/v1/checklist-mappings/by-product/?product_id=${productId}`,
      providesTags: (result, error, productId) => [
        { type: "ChecklistMappings", id: `product-${productId}` },
      ],
    }),
    bulkCreateChecklistMappings: builder.mutation({
      query: (body: {
        product_id: number;
        checklist_items: Array<{
          checklist_id: number;
          is_required?: boolean;
          status?: string;
        }>;
      }) => ({
        url: "/inventory/api/v1/checklist-mappings/bulk-create/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ChecklistMappings", id: `product-${arg.product_id}` },
      ],
    }),
    deleteChecklistMapping: builder.mutation({
      query: (id: number) => ({
        url: `/inventory/api/v1/checklist-mappings/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ChecklistMappings" }],
    }),

    // Checklist Documents (actual uploads)
    uploadChecklistDocument: builder.mutation({
      query: (body: FormData) => ({
        url: "/inventory/api/v1/checklist-documents/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ChecklistDocuments" }, { type: "ChecklistMappings" }],
    }),

    // Purchase Request & Order Document Checklist views
    getPurchaseRequestDocumentsByPurchaseRequest: builder.query({
      query: (purchaseRequestId: number | string) =>
        `/inventory/api/v1/purchase-request-documents/by-purchase-request/?purchase_request_id=${purchaseRequestId}`,
      providesTags: (result, error, purchaseRequestId) => [
        { type: "ChecklistDocuments", id: `pr-${purchaseRequestId}` },
      ],
    }),
    getPurchaseRequestDocumentsByPurchaseOrder: builder.query({
      query: (params: { purchase_order_id?: number | string; purchase_order_number?: string }) => {
        const { purchase_order_id, purchase_order_number } = params || {};
        const qs =
          purchase_order_id != null
            ? `purchase_order_id=${purchase_order_id}`
            : `purchase_order_number=${encodeURIComponent(purchase_order_number || "")}`;
        return `/inventory/api/v1/purchase-request-documents/by-purchase-order/?${qs}`;
      },
      providesTags: (result, error, params) => [
        {
          type: "ChecklistDocuments",
          id: params?.purchase_order_id
            ? `po-${params.purchase_order_id}`
            : `po-number-${params?.purchase_order_number}`,
        },
      ],
    }),

    //Product Attachments

    getAllProductAttachments: builder.query<any[], string | undefined>({
      query: (id) => `/inventory/api/v1/product_attachments/?project=${id}`,
    }),

    createProductAttachment: builder.mutation({
      query: (formData: FormData) => ({
        url: "/inventory/api/v1/product_image_attachments/",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),

    createInventoryImageAttachment: builder.mutation({
      query: (formData: FormData) => ({
        url: "/inventory/api/v1/product_image_attachments/",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),
    updateInventoryImageAttachment: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/product_image_attachments/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),
    removeProductImageAssignment: builder.mutation({
      query: ({ imageId, productId }: { imageId: number; productId: number }) => ({
        url: `/inventory/api/v1/product_image_attachments/${imageId}/remove-product/`,
        method: "POST",
        body: { product_id: productId },
      }),
      invalidatesTags: ["GetInventoryProducts"],
    }),
    getInventoryImageAttachmentById: builder.query({
      query: (id) => `/inventory/api/v1/product_image_attachments/${id}/`,
    }),

    getInventoryImageAttachments: builder.query({
      query: (page) =>
        `/inventory/api/v1/product_image_attachments/?page=${page.page}&page_size=${page.pageSize}&search=${page.search || ""}`,
    }),

    // getInventoryImageAttachments: builder.query({
    //   query: () => "/inventory/api/v1/product_image_attachments/",
    // }),

    // createProductAttachment: builder.mutation({
    //     query: (formData) => {
    //         return {
    //             url: "/inventory/api/v1/product_attachments/",
    //             method: "POST",
    //             body: formData,
    //         };
    //     },
    //     invalidatesTags: ["GetInventoryProducts"],
    // }),

    deleteProductAttachmentById: builder.mutation({
      query: (id: string) => ({
        url: `/inventory/api/v1/product_image_attachments/${id}/`,
        method: "DELETE",
      }),
    }),
    //end point

    //Supplier Cost Mapping
    getSupplierCostMapping: builder.query({
      query: () => "/inventory/api/v1/supplier-product-cost-mapping/",
    }),
    getSupplierCostMappingById: builder.query({
      query: (id) => `/inventory/api/v1/supplier-product-cost-mapping/${id}/`,
    }),
    getSupplierProductsById: builder.query({
      query: (id) =>
        `/inventory/api/v1/supplier-product-cost-mapping/supplier-products/${id}/`,
    }),
    getSupplierforProductById: builder.query({
      query: (id) =>
        `/inventory/api/v1/supplier-product-cost-mapping/product-suppliers/${id}/`,
    }),
    getProductsList: builder.query({
      query: ({ search }) => {
        const baseUrl = "/inventory/api/v1/products/list-products/";
        // If search is empty or undefined, just call the base URL
        return search ? `${baseUrl}?q=${encodeURIComponent(search)}` : baseUrl;
      },
    }),
    createSupplierCostMapping: builder.mutation({
      query: (values) => ({
        url: "/inventory/api/v1/supplier-product-cost-mapping/",
        method: "POST",
        body: values,
      }),
    }),
    updateSupplierCostMapping: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/supplier-product-cost-mapping/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),
    deleteSupplierCostMappingById: builder.mutation({
      query: (id) => ({
        url: `/inventory/api/v1/supplier-product-cost-mapping/${id}/`,
        method: "DELETE",
      }),
    }),

    getProductsSupplierById: builder.query({
      query: (id) => `/inventory/api/v1/supplier-suggestions/${id}/`,
    }),
    createSuppliersAutoMap: builder.mutation({
      query: (values) => ({
        url: "/inventory/api/v1/auto-map-suppliers/",
        method: "POST",
        body: values,
      }),
    }),

    applyComparisonSelection: builder.mutation({
      query: ({
        purchaseRequestId,
        items,
      }: {
        purchaseRequestId: number | string;
        items: {
          purchase_request_item_id: number;
          supplier_id?: number;
          unit_price?: string | number;
          discount?: string | number;
          source_mode?: "supplier" | "stock" | "split";
          stock_source_id?: number;
          stock_qty?: string | number;
          supplier_qty?: string | number;
          notes?: string;
        }[];
      }) => ({
        url: `/inventory/api/v1/purchase-requests/${purchaseRequestId}/apply-comparison-selection/`,
        method: "POST",
        body: { items },
      }),
    }),

    getPurchaseOrders: builder.query({
      query: (page) =>
        `/inventory/api/v1/purchase-orders/?page=${page.page}&page_size=${
          page.pageSize
        }&po_status=${page.po_status || ""}&delivery_status=${
          page.delivery_status || ""
        }&search=${page.search || ""}&project=${page.project || ""}`,
      providesTags: ["GetPurchaseOrders"],
    }),

    // getPurchaseOrder: builder.query({
    //   query: (page) => `/inventory/api/v1/purchase-orders/?page=${page.page}&page_size=${page.pageSize}`,
    //   // providesTags: ["GetInventoryProducts"],
    // }),

    // Inventory Purchase Order
    // getPurchaseOrder: builder.query({
    //   query: (page) => `/inventory/api/v1/purchase-orders/?page=${page.page}&page_size=${page.pageSize}`,,
    // }),

    getPurchaseOrderById: builder.query({
      query: (id) => `/inventory/api/v1/purchase-orders/by-number/${id}/`,
      providesTags: (id) => [{ type: "GetPurchaseOrderByNumber", id: id }],
    }),
    getPurchaseOrderByRequestId: builder.query({
      query: (id) =>
        `/inventory/api/v1/purchase-orders/by-purchase-request/${id}/`,
      providesTags: (id) => [{ type: "GetPurchaseOrderByRequestId", id: id }],
    }),
    createPurchaseOrder: builder.mutation({
      query: (values) => ({
        url: "/inventory/api/v1/purchase-orders/",
        method: "POST",
        body: values,
      }),
      invalidatesTags: ["GetPurchaseOrders"],
    }),
    updatePurchaseOrder: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/purchase-orders/by-number/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),

    updatePurchaseOrderById: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/purchase-orders/${id}/`,
        method: "PATCH",
        body: values,
      }),
    }),

    updatePurchaseOrderItemByID: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/purchase-order-item-updates/single-update/`,
        method: "PATCH",
        body: values,
      }),
    }),
    bulkUpdatePurchaseOrderDeliveryType: builder.mutation({
      query: (values: {
        delivery_type: "planned" | "in_preparation";
        item_ids?: number[];
        split_ids?: number[];
      }) => ({
        url: "/inventory/api/v1/purchase-order-item-updates/bulk-delivery-type/",
        method: "PATCH",
        body: values,
      }),
    }),

    getAllPurchaseAttachments: builder.query<any[], string | undefined>({
      query: (id) =>
        `/inventory/api/v1/purchase-request-attachments/?project=${id}`,
    }),

    createPurchaseAttachment: builder.mutation({
      query: (formData: FormData) => ({
        url: "/inventory/api/v1/purchase-request-attachments/",
        method: "POST",
        body: formData,
      }),
    }),

    deletePurchaseAttachmentById: builder.mutation({
      query: (id: string) => ({
        url: `/inventory/api/v1/purchase-request-attachments/${id}/`,
        method: "DELETE",
      }),
    }),

    createPurchaseOrderDeliveryItemSplit: builder.mutation({
      query: (values) => ({
        url: "/inventory/api/v1/purchase-order-item-updates/split-delivery/",
        method: "POST",
        body: values,
      }),
      invalidatesTags: ["GetPurchaseOrders"],
    }),

    updatePurchaseOrderDeliveryItemSplitItemByID: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/purchase-order-item-updates/update-split-delivery/`,
        method: "PATCH",
        body: values,
      }),
    }),

    updatePurchaseOrderReDeliveryItemByID: builder.mutation({
      query: ({ id, values }) => ({
        url: `/inventory/api/v1/purchase-order-item-updates/re-delivery/`,
        method: "PATCH",
        body: values,
      }),
    }),

    // PO cut-off flow: mark items as undeliverable
    markUndeliverable: builder.mutation({
      query: (body: {
        po_id: number;
        item_ids: number[];
        undeliverable_notes?: string;
        item_used_amounts?: Record<string, number>;
      }) => ({
        url: "/inventory/api/v1/purchase-order-item-updates/mark-undeliverable/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetPurchaseOrderByNumber", id: arg.po_id },
        "GetPurchaseOrders",
      ],
    }),

    // PO cut-off flow: create new PO from undeliverable items
    createPOFromUndeliverable: builder.mutation({
      query: ({
        poId,
        body,
      }: {
        poId: number;
        body: {
          new_supplier_id: number;
          item_ids?: number[];
          // legacy per-item API (no longer used in UI, kept for compatibility)
          item_costs?: Array<{ item_id: number; unit_price: number }>;
          // new simple flow: single split amount taken from remaining balance
          split_amount?: number;
          notes?: string;
          // Optional flag: also update SupplierProductCostMapping from these amounts
          update_cost_mapping?: boolean;
        };
      }) => ({
        url: `/inventory/api/v1/purchase-orders/${poId}/create-po-from-undeliverable/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetPurchaseOrders"],
    }),

    // PO cut-off flow: split undeliverable into multiple suppliers/POs
    splitUndeliverable: builder.mutation({
      query: ({
        poId,
        body,
      }: {
        poId: number;
        body: {
          splits: Array<{
            supplier_id: number;
            notes?: string;
            items: Array<{ parent_item_id: number; amount: number }>;
          }>;
          update_cost_mapping?: boolean;
        };
      }) => ({
        url: `/inventory/api/v1/purchase-orders/${poId}/split-undeliverable/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetPurchaseOrders"],
    }),

    // Cancellation flow: cancel a Purchase Request (blocked if it has active POs)
    cancelPurchaseRequest: builder.mutation({
      query: ({ id, reason, cancelled_by_user_id }: { id: number; reason: string; cancelled_by_user_id?: number | string }) => ({
        url: `/inventory/api/v1/purchase-requests/${id}/cancel/`,
        method: "POST",
        body: { reason, cancelled_by_user_id },
      }),
      invalidatesTags: ["GetInventoryProducts", "GetPurchaseOrders", "GetPurchaseOrderByRequestId"],
    }),

    // Cancellation flow: cancel a Purchase Order (blocked if it has active DRs); returns budget + emails supplier
    cancelPurchaseOrder: builder.mutation({
      query: ({ id, reason, cancelled_by_user_id }: { id: number; reason: string; cancelled_by_user_id?: number | string }) => ({
        url: `/inventory/api/v1/purchase-orders/${id}/cancel/`,
        method: "POST",
        body: { reason, cancelled_by_user_id },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetPurchaseOrderByNumber", id: arg.id },
        "GetPurchaseOrders",
        "GetPurchaseOrderByRequestId",
      ],
    }),

    // Cancellation flow: cancel a Delivery Request (split delivery OR non-split item); emails supplier
    cancelDeliveryRequest: builder.mutation({
      query: ({
        split_id,
        item_id,
        reason,
        cancelled_by_user_id,
      }: {
        split_id?: number;
        item_id?: number;
        reason: string;
        cancelled_by_user_id?: number | string;
      }) => ({
        url: `/inventory/api/v1/purchase-order-item-updates/cancel-delivery-request/`,
        method: "POST",
        body: { split_id, item_id, reason, cancelled_by_user_id },
      }),
      invalidatesTags: ["GetPurchaseOrders", "GetPurchaseOrderByNumber"],
    }),

    // PO Update flow: increase/reduce a PO item quantity (syncs cost + budget, optional re-approval)
    updatePurchaseOrderItemQuantity: builder.mutation({
      query: ({
        id,
        item_id,
        new_quantity,
        needs_reapproval,
        reason,
        delivery_action,
        new_delivery_date,
      }: {
        id: number;
        item_id: number;
        new_quantity: number;
        needs_reapproval?: boolean;
        reason?: string;
        delivery_action?: "merge" | "new_record";
        new_delivery_date?: string;
      }) => ({
        url: `/inventory/api/v1/purchase-orders/${id}/update-item-quantity/`,
        method: "POST",
        body: {
          item_id,
          new_quantity,
          needs_reapproval,
          reason,
          delivery_action,
          new_delivery_date,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetPurchaseOrderByNumber", id: arg.id },
        "GetPurchaseOrders",
        "GetPurchaseOrderByRequestId",
        "GetPurchaseOrderAuditLogs",
      ],
    }),

    // PO Update flow: audit log timeline (with before/after PDF versions) for a PO
    getPurchaseOrderAuditLogs: builder.query({
      query: (id: number | string) =>
        `/inventory/api/v1/purchase-orders/${id}/audit-logs/`,
      providesTags: ["GetPurchaseOrderAuditLogs"],
    }),

    // Delivery flow: transfer still-open delivery items to a new PO (new supplier, no approval, auto-email)
    transferDeliveryItemsToNewPo: builder.mutation({
      query: ({
        id,
        new_supplier_id,
        items,
        delivery_date,
        notes,
      }: {
        id: number;
        new_supplier_id: number;
        items: { item_id: number; quantity: number; split_id?: number }[];
        delivery_date?: string;
        notes?: string;
      }) => ({
        url: `/inventory/api/v1/purchase-orders/${id}/transfer-to-new-po/`,
        method: "POST",
        body: { new_supplier_id, items, delivery_date, notes },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetPurchaseOrderByNumber", id: arg.id },
        "GetPurchaseOrders",
        "GetPurchaseOrderByRequestId",
        "GetPurchaseOrderAuditLogs",
      ],
    }),

    getPruchaseOrdersByProductAndSupplier: builder.mutation({
      query: ({
        supplier_id,
        product_id,
        page = 1,
        page_size = 5,
      }: {
        supplier_id: number | string;
        product_id: number | string;
        page?: number;
        page_size?: number;
      }) => ({
        url: `/inventory/api/v1/purchase-orders/supplier-product-orders/?supplier_id=${supplier_id}&product_id=${product_id}&page=${page}&page_size=${page_size}`,
        method: "GET",
      }),
    }),

    // Pdf of  purchase order and purchase request

    getPurchaseRequestDownloadUids: builder.mutation({
      query: (arg: any) => {
        const report_no = typeof arg === "string" ? arg : arg?.report_no;
        const cancellation = typeof arg === "object" && arg?.cancellation;
        return {
          url: `/generator/api/v1/purchase-request-pdf/${report_no}${cancellation ? "?cancellation=1" : ""}`,
          method: "GET",
        };
      },
    }),

    getPurchaseOrderDownloadUids: builder.mutation({
      query: (arg: any) => {
        const report_no = typeof arg === "string" ? arg : arg?.report_no;
        const cancellation = typeof arg === "object" && arg?.cancellation;
        return {
          url: `/generator/api/v1/purchase-order-pdf/${report_no}${cancellation ? "?cancellation=1" : ""}`,
          method: "GET",
        };
      },
    }),
    getDeliveryRequestDownloadUids: builder.mutation({
      query: ({ report_no, is_defect_items, delivery_date, item_id, split_id, cancellation }: any) => ({
        url: `/generator/api/v1/delivery-requisition-pdf/${report_no}/?is_defect_items=${is_defect_items}${delivery_date ? `&delivery_date=${encodeURIComponent(delivery_date)}` : ""}${item_id ? `&item_id=${item_id}` : ""}${split_id ? `&split_id=${split_id}` : ""}${cancellation ? `&cancellation=1` : ""}`,
        method: "GET",
      }),
    }),

    // Stock Management APIs
    getStockManagement: builder.query({
      query: (page) =>
        `inventory/api/v1/stock-management/?page=${page.page}&page_size=${page.pageSize}&search=${page.search || ""}`,
      providesTags: ["GetStockDetails"],
    }),

    getStockManagementById: builder.query({
      query: (id) => `inventory/api/v1/stock-management/${id}/`,
      providesTags: (result, error, id) => [
        { type: "GetStockManagementByNumber", id: id },
      ],
    }),
    getAllocatableStockForProject: builder.query({
      query: ({
        target_project_id,
        product_id,
      }: {
        target_project_id: number | string;
        product_id: number | string;
      }) =>
        `inventory/api/v1/stock-management/allocatable-for-project/?target_project_id=${target_project_id}&product_id=${product_id}`,
      providesTags: ["GetStockManagement", "GetStockDetails"],
    }),
    getProjectTransferCapacity: builder.query({
      query: ({
        project_id,
        product_id,
      }: {
        project_id: number | string;
        product_id: number | string;
      }) =>
        `inventory/api/v1/stock-management/project-transfer-capacity/?project_id=${project_id}&product_id=${product_id}`,
    }),

    createStockManagement: builder.mutation({
      query: (payload) => ({
        url: "inventory/api/v1/stock-management/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetStockDetails"],
    }),

    updateStockManagement: builder.mutation({
      query: ({ id, productData }) => ({
        url: `inventory/api/v1/stock-management/${id}/`,
        method: "PATCH",
        body: productData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "GetStockManagementByNumber", id: id },
        "GetStockManagement",
        "GetStockDetails",
      ],
    }),

    // deleteStockManagementById: builder.mutation({
    //   query: (id) => ({
    //     url: `inventory/api/v1/stock-management/${id}/`, // Fixed URL path
    //     method: "DELETE",
    //   }),
    //   invalidatesTags: ["GetStockManagement"],
    // }),

    //Stock-Allocation

    getStockProjectAllocationById: builder.query({
      query: (id) => `inventory/api/v1/stock-project-allocations/${id}/`,
      providesTags: (result, error, id) => [
        { type: "StockProjectAllocation", id: id },
      ],
    }),

    createStockProjectAllocation: builder.mutation({
      query: (payload) => ({
        url: "inventory/api/v1/stock-project-allocations/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        "StockProjectAllocations",
        "GetStockManagement",
        "GetStockDetails",
      ],
    }),

    updateStockProjectAllocation: builder.mutation({
      query: ({ id, allocationData }) => ({
        url: `inventory/api/v1/stock-project-allocations/${id}/`,
        method: "PATCH",
        body: allocationData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StockProjectAllocation", id: id },
        "StockProjectAllocations",
        "GetStockManagement",
        "GetStockDetails",
      ],
    }),

    deleteStockProjectAllocation: builder.mutation({
      query: (id) => ({
        url: `inventory/api/v1/stock-project-allocations/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "StockProjectAllocations",
        "GetStockManagement",
        "GetStockDetails",
      ],
    }),

    // New stock allocation management (PR/PO linked transfer flow)
    createStockAllocationManagement: builder.mutation({
      query: (payload) => ({
        url: "inventory/api/v1/stock-allocation-management/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["StockAllocationManagement", "GetStockManagement", "GetStockDetails"],
    }),
    getStockAllocationManagement: builder.query({
      query: (params?: {
        page?: number;
        pageSize?: number;
        transfer_status?: string;
        purchase_order?: number | string;
        purchase_request?: number | string;
      }) => {
        const page = params?.page ?? 1;
        const pageSize = params?.pageSize ?? 30;
        const transferStatus = params?.transfer_status ? `&transfer_status=${params.transfer_status}` : "";
        const purchaseOrder = params?.purchase_order ? `&purchase_order=${params.purchase_order}` : "";
        const purchaseRequest = params?.purchase_request ? `&purchase_request=${params.purchase_request}` : "";
        return `inventory/api/v1/stock-allocation-management/?page=${page}&page_size=${pageSize}${transferStatus}${purchaseOrder}${purchaseRequest}`;
      },
      providesTags: ["StockAllocationManagement"],
    }),
    updateStockAllocationTransferStatus: builder.mutation({
      query: ({
        id,
        values,
        attachments,
      }: {
        id: number | string;
        values: {
          transfer_status: "issued" | "in_transit" | "received" | "partial_received" | "cancelled";
          quantity?: string | number;
          notes?: string;
          delivery_person_contact?: string;
          delivery_date?: string;
          items_arrive_from?: string;
        };
        attachments?: File[];
      }) => {
        // Send multipart when attachments are present so the delivery proof uploads.
        if (attachments && attachments.length > 0) {
          const formData = new FormData();
          Object.entries(values).forEach(([k, v]) => {
            if (v !== undefined && v !== null) formData.append(k, String(v));
          });
          attachments.forEach((f) => formData.append("attachments", f));
          return {
            url: `inventory/api/v1/stock-allocation-management/${id}/update-transfer-status/`,
            method: "POST",
            body: formData,
          };
        }
        return {
          url: `inventory/api/v1/stock-allocation-management/${id}/update-transfer-status/`,
          method: "POST",
          body: values,
        };
      },
      invalidatesTags: ["StockAllocationManagement", "GetPurchaseOrders", "GetStockManagement", "GetStockDetails"],
    }),

    // Step 1 of stock-transfer receive: capture delivery details (contact, date, arrive-from)
    updateStockAllocationDeliveryDetails: builder.mutation({
      query: ({
        id,
        values,
        attachments,
      }: {
        id: number | string;
        values: {
          delivery_person_contact: string;
          delivery_date: string;
          items_arrive_from: string;
        };
        attachments?: File[];
      }) => {
        const formData = new FormData();
        Object.entries(values).forEach(([k, v]) => {
          if (v !== undefined && v !== null) formData.append(k, String(v));
        });
        (attachments || []).forEach((f) => formData.append("attachments", f));
        return {
          url: `inventory/api/v1/stock-allocation-management/${id}/update-delivery-details/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["StockAllocationManagement", "GetPurchaseOrders", "GetStockManagement", "GetStockDetails"],
    }),

    // Send RFQ Email
    sendRfqEmail: builder.mutation({
      query: ({ purchase_request_id, supplier_id, supplier_ids, subject, body, cc_emails, attachment_format, selected_item_ids, extra_attachment }) => {
        const formData = new FormData();
        const ids = supplier_ids || (supplier_id ? [supplier_id] : []);
        ids.forEach((id: number) => formData.append("supplier_ids", String(id)));
        if (subject) formData.append("subject", subject);
        if (body) formData.append("body", body);
        (cc_emails || []).forEach((e: string) => formData.append("cc_emails", e));
        formData.append("attachment_format", attachment_format || "excel");
        (selected_item_ids || []).forEach((id: number) => formData.append("selected_item_ids", String(id)));
        if (extra_attachment) formData.append("extra_attachment", extra_attachment);
        return {
          url: `/inventory/api/v1/purchase-requests/${purchase_request_id}/send-rfq-email/`,
          method: "POST",
          body: formData,
        };
      },
    }),

    // Send Purchase Order Email
    sendPurchaseOrderEmail: builder.mutation({
      query: ({ purchase_order_id, to_emails, cc_emails, subject, body, attach_pdf, attachments }) => {
        // Multipart when user attachments are present
        if (attachments && attachments.length > 0) {
          const formData = new FormData();
          (to_emails || []).forEach((e: string) => formData.append("to_emails", e));
          (cc_emails || []).forEach((e: string) => formData.append("cc_emails", e));
          if (subject) formData.append("subject", subject);
          if (body) formData.append("body", body);
          formData.append("attach_pdf", String(attach_pdf !== false));
          attachments.forEach((f: File) => formData.append("attachments", f));
          return {
            url: `/inventory/api/v1/purchase-orders/${purchase_order_id}/send-email/`,
            method: "POST",
            body: formData,
          };
        }
        return {
          url: `/inventory/api/v1/purchase-orders/${purchase_order_id}/send-email/`,
          method: "POST",
          body: {
            to_emails: to_emails || [],
            cc_emails: cc_emails || [],
            subject,
            body,
            attach_pdf: attach_pdf !== false,
          },
        };
      },
    }),

    // Send Delivery Requisition Email
    sendDeliveryRequisitionEmail: builder.mutation({
      query: ({ purchase_order_id, to_emails, cc_emails, subject, body, attach_pdf, is_defect_items, delivery_date, item_id, split_id, attachments }) => {
        // Multipart when user attachments are present
        if (attachments && attachments.length > 0) {
          const formData = new FormData();
          (to_emails || []).forEach((e: string) => formData.append("to_emails", e));
          (cc_emails || []).forEach((e: string) => formData.append("cc_emails", e));
          if (subject) formData.append("subject", subject);
          if (body) formData.append("body", body);
          formData.append("attach_pdf", String(attach_pdf !== false));
          formData.append("is_defect_items", String(is_defect_items || false));
          if (delivery_date) formData.append("delivery_date", delivery_date);
          if (item_id) formData.append("item_id", String(item_id));
          if (split_id) formData.append("split_id", String(split_id));
          attachments.forEach((f: File) => formData.append("attachments", f));
          return {
            url: `/inventory/api/v1/purchase-orders/${purchase_order_id}/send-delivery-email/`,
            method: "POST",
            body: formData,
          };
        }
        return {
          url: `/inventory/api/v1/purchase-orders/${purchase_order_id}/send-delivery-email/`,
          method: "POST",
          body: {
            to_emails: to_emails || [],
            cc_emails: cc_emails || [],
            subject,
            body,
            attach_pdf: attach_pdf !== false,
            is_defect_items: is_defect_items || false,
            delivery_date: delivery_date || null,
            item_id: item_id || null,
            split_id: split_id || null,
          },
        };
      },
    }),
  }),
});

export const {
  //Categories
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryByIdMutation,

  //Measurement
  useGetMeasurementsQuery,
  useGetMeasurementByIdQuery,
  useCreateMeasurementMutation,
  useUpdateMeasurementMutation,
  useDeleteMeasurementByIdMutation,

  //Supplier
  useGetSuppliersQuery,
  useGetSupplierByIdQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierByIdMutation,

  //Products
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductByIdMutation,

  //tags
  useGetTagsQuery,
  useGetTagsByIdQuery,
  useCreateTagsMutation,
  useUpdateTagsMutation,
  useDeleteTagsByIdMutation,

  // PDF Metadata
  useGetPdfMetadataListQuery,
  useGetPdfMetadataByIdQuery,
  useCreatePdfMetadataMutation,
  useUpdatePdfMetadataMutation,
  useDeletePdfMetadataMutation,

  // Document Checklist
  useGetDocumentChecklistQuery,
  useGetDocumentChecklistByIdQuery,
  useCreateDocumentChecklistMutation,
  useUpdateDocumentChecklistMutation,
  useDeleteDocumentChecklistMutation,

  // Checklist Mappings
  useGetChecklistMappingsByProductQuery,
  useBulkCreateChecklistMappingsMutation,
  useDeleteChecklistMappingMutation,

  // Checklist Documents
  useUploadChecklistDocumentMutation,

  // Purchase Request / Order Document Checklist views
  useGetPurchaseRequestDocumentsByPurchaseRequestQuery,
  useGetPurchaseRequestDocumentsByPurchaseOrderQuery,

  //Attachments
  useCreateProductAttachmentMutation,
  useDeleteProductAttachmentByIdMutation,
  useGetAllProductAttachmentsQuery,

  //Purchase Request
  useGetPurchaseRequestByIdQuery,
  useCreatePurchaseRequestItemsMutation,
  useBulkMarkRfqMutation,
  useUpdatePurchaseRequestItemsMutation,
  useCreatePurchaseRequestMutation,
  useDeletePurchaseRequestByIdMutation,
  useDeletePurchaseRequestItemsByIdMutation,
  useGetPurchaseRequestItemsByIdQuery,
  useGetPurchaseRequestItemsQuery,
  useGetPurchaseRequestsQuery,
  useUpdatePurchaseRequestMutation,

  //Purchase Request Attachments
  useGetAllPurchaseAttachmentsQuery,
  useCreatePurchaseAttachmentMutation,
  useDeletePurchaseAttachmentByIdMutation,

  //StockManagement
  useCreateStockManagementMutation,
  useGetStockManagementQuery,
  useGetStockManagementByIdQuery,
  useGetAllocatableStockForProjectQuery,
  useGetProjectTransferCapacityQuery,
  useUpdateStockManagementMutation,

  //StockAllocations
  useCreateStockProjectAllocationMutation,
  useDeleteStockProjectAllocationMutation,
  useGetStockProjectAllocationByIdQuery,
  useUpdateStockProjectAllocationMutation,
  useCreateStockAllocationManagementMutation,
  useGetStockAllocationManagementQuery,
  useUpdateStockAllocationTransferStatusMutation,
  useUpdateStockAllocationDeliveryDetailsMutation,

  //Supplier Cost Mapping
  useGetProductsListQuery,
  useGetSupplierCostMappingByIdQuery,
  useGetSupplierCostMappingQuery,
  useGetSupplierProductsByIdQuery,
  useGetSupplierforProductByIdQuery,
  useCreateSupplierCostMappingMutation,
  useUpdateSupplierCostMappingMutation,
  useDeleteSupplierCostMappingByIdMutation,

  useGetProductsSupplierByIdQuery,
  useCreateSuppliersAutoMapMutation, // Auto-mapping suppliers

  useApplyComparisonSelectionMutation,

  //Inventory Purchase Order
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderByIdQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useGetPurchaseOrderByRequestIdQuery,
  useUpdatePurchaseOrderByIdMutation,
  useUpdatePurchaseOrderItemByIDMutation,
  useBulkUpdatePurchaseOrderDeliveryTypeMutation,
  useCreatePurchaseOrderDeliveryItemSplitMutation,
  useUpdatePurchaseOrderDeliveryItemSplitItemByIDMutation,
  useUpdatePurchaseOrderReDeliveryItemByIDMutation,
  useGetPurchaseOrderDownloadUidsMutation,
  useGetPurchaseRequestDownloadUidsMutation,
  useGetPruchaseOrdersByProductAndSupplierMutation,
  useGetDeliveryRequestDownloadUidsMutation,

  // PO cut-off flow
  useMarkUndeliverableMutation,
  useCreatePOFromUndeliverableMutation,
  useSplitUndeliverableMutation,
  useCancelPurchaseRequestMutation,
  useCancelPurchaseOrderMutation,
  useCancelDeliveryRequestMutation,

  // PO Update flow
  useUpdatePurchaseOrderItemQuantityMutation,
  useGetPurchaseOrderAuditLogsQuery,
  useTransferDeliveryItemsToNewPoMutation,

  //Inventory Product Attachment
  useCreateInventoryImageAttachmentMutation,
  useUpdateInventoryImageAttachmentMutation,
  useRemoveProductImageAssignmentMutation,
  useGetInventoryImageAttachmentByIdQuery,
  useGetInventoryImageAttachmentsQuery,

  //RFQ Email
  useSendRfqEmailMutation,

  //PO & Delivery Email
  useSendPurchaseOrderEmailMutation,
  useSendDeliveryRequisitionEmailMutation,
} = resourceInventoryManagementSlice;
